import { client, handleRequestBody } from "./client.ts";

export type ChartInterval = "weekly" | "monthly";

export interface ChartCurvePoint {
	period: string;
	label: string;
	planned_percent: number;
	actual_percent: number | null;
	actual_as_of: string | null;
}

export interface ChartStageBar {
	stage_id: string;
	name: string;
	planned_percent: number;
	actual_percent: number;
	has_children: boolean;
}

export interface ConstructionProgressResponse {
	reference_date: string;
	planned_percent: number;
	actual_percent: number;
	deviation_points: number;
	delayed_count: number;
	no_baseline: boolean;
	curve: ChartCurvePoint[];
	stages: ChartStageBar[];
}

export interface GetConstructionProgressParams {
	interval?: ChartInterval;
	referenceDate?: string;
	stageId?: string;
}

export function getConstructionProgress(
	constructionId: string,
	params: GetConstructionProgressParams = {},
): Promise<ConstructionProgressResponse> {
	return handleRequestBody(() =>
		client.get<ConstructionProgressResponse>(
			`/constructions/${constructionId}/charts/progress`,
			{
				params: {
					interval: params.interval,
					reference_date: params.referenceDate,
					stage_id: params.stageId,
				},
			},
		),
	);
}

export interface ChartStatusCount {
	status: string;
	total: number;
}

export interface ChartDelayedStage {
	stage_id: string;
	name: string;
	planned_percent: number;
	actual_percent: number;
	delay_points: number;
	planned_end_date: string;
}

export interface StageStatusResponse {
	reference_date: string;
	statuses: ChartStatusCount[];
	delayed_count: number;
	delayed: ChartDelayedStage[];
}

export interface GetStageStatusParams {
	referenceDate?: string;
}

export function getStageStatus(
	constructionId: string,
	params: GetStageStatusParams = {},
): Promise<StageStatusResponse> {
	return handleRequestBody(() =>
		client.get<StageStatusResponse>(
			`/constructions/${constructionId}/charts/stage-status`,
			{ params: { reference_date: params.referenceDate } },
		),
	);
}

export interface ChartMeasurementPeriod {
	period: string;
	label: string;
	count: number;
	gain_points: number;
}

export interface MeasurementHistoryResponse {
	from: string;
	to: string;
	interval: ChartInterval;
	periods: ChartMeasurementPeriod[];
}

export interface GetMeasurementHistoryParams {
	from?: string;
	to?: string;
	interval?: ChartInterval;
	referenceDate?: string;
}

export function getMeasurementHistory(
	constructionId: string,
	params: GetMeasurementHistoryParams = {},
): Promise<MeasurementHistoryResponse> {
	return handleRequestBody(() =>
		client.get<MeasurementHistoryResponse>(
			`/constructions/${constructionId}/charts/measurements`,
			{
				params: {
					from: params.from,
					to: params.to,
					interval: params.interval,
					reference_date: params.referenceDate,
				},
			},
		),
	);
}

export interface ChartAttendanceDay {
	date: string;
	total: number;
}

export interface AttendanceResponse {
	from: string;
	to: string;
	headcount: number;
	days: ChartAttendanceDay[];
}

export interface GetAttendanceParams {
	from?: string;
	to?: string;
	referenceDate?: string;
}

export function getAttendance(
	worksiteId: string,
	params: GetAttendanceParams = {},
): Promise<AttendanceResponse> {
	return handleRequestBody(() =>
		client.get<AttendanceResponse>(
			`/worksites/${worksiteId}/charts/attendance`,
			{
				params: {
					from: params.from,
					to: params.to,
					reference_date: params.referenceDate,
				},
			},
		),
	);
}

export interface ChartPortfolioEntry {
	construction_id: string;
	name: string;
	planned_percent: number;
	actual_percent: number;
	deviation_points: number;
	delayed_count: number;
	last_measured_on: string | null;
	no_baseline: boolean;
}

export interface PortfolioResponse {
	reference_date: string;
	constructions: number;
	delayed_count: number;
	planned_percent: number;
	actual_percent: number;
	entries: ChartPortfolioEntry[];
}

export interface GetPortfolioParams {
	referenceDate?: string;
}

export function getPortfolio(
	worksiteId: string,
	params: GetPortfolioParams = {},
): Promise<PortfolioResponse> {
	return handleRequestBody(() =>
		client.get<PortfolioResponse>(`/worksites/${worksiteId}/charts/portfolio`, {
			params: { reference_date: params.referenceDate },
		}),
	);
}
